import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanSearch, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { useRunScan, useScanDetail } from "../../hooks/useScan";
import { errorMessage } from "../../hooks/useServers";

function Line({ label, children }) {
  if (!children || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <p className="text-xs">
      <span className="text-muted-foreground">{label}: </span>
      {children}
    </p>
  );
}

function ScanDetail({ projectId, server }) {
  const { data, isLoading } = useScanDetail(projectId, server.id, true);
  if (isLoading) return <p className="text-xs text-muted-foreground">Cargando detalle...</p>;
  if (!data) return null;
  const s = data.summary;
  const cfg = data.db_config || {};
  const tools = Object.entries(s.herramientas || {}).filter(([, v]) => v).map(([k]) => k);
  return (
    <div data-testid="scan-detail" className="rounded-md border border-border p-2 space-y-1">
      <Line label="Sistema">{[s.sistema?.os, s.sistema?.kernel && `kernel ${s.sistema.kernel}`].filter(Boolean).join(", ")}</Line>
      <Line label="Recursos">
        {s.recursos?.cpus ? `${s.recursos.cpus} CPU · RAM ${s.recursos.ram_usada_mb}/${s.recursos.ram_total_mb} MB · disco ${s.recursos.disco_usado}/${s.recursos.disco_total} (${s.recursos.disco_pct})` : null}
      </Line>
      <Line label="Herramientas">{tools.join(", ")}{s.herramientas && !s.herramientas.mongosh ? " (sin mongosh)" : ""}</Line>
      <Line label="pm2">{(s.pm2 || []).map((p) => `${p.name} [${p.status || "?"}]`).join("; ")}</Line>
      <Line label="Docker">{(s.docker?.contenedores || []).map((c) => `${c.nombre} (${c.imagen})`).join("; ")}</Line>
      <Line label="Puertos">
        {(s.puertos || []).filter((p) => ![22, 53, 80, 443].includes(p.puerto)).slice(0, 10).map((p) => `${p.direccion} ${p.proceso || ""}`.trim()).join("; ")}
      </Line>
      <Line label="Dominios">{(s.nginx?.dominios || []).join(", ")}</Line>
      <Line label="Seguridad">
        {`ufw ${s.seguridad?.ufw || "sin datos"} · fail2ban ${(s.seguridad?.fail2ban_jaulas || []).join(", ") || "sin datos"}`}
      </Line>
      {(s.codigo || []).slice(0, 5).map((f) => (
        <Line key={f.ruta} label={`Código${f.viva ? " (en uso)" : ""}`}>
          {`${f.ruta} — ${(f.stack || []).join(", ") || "stack ?"}${f.env_variables?.length ? ` · variables: ${f.env_variables.slice(0, 12).join(", ")}` : ""}`}
        </Line>
      ))}
      <Line label="Bases de datos">{(s.bases_de_datos || []).map((d) => `${d.tipo} ${d.ubicacion}${d.version ? ` v${d.version}` : ""}`).join("; ")}</Line>
      <Line label="Consulta de BD">
        {cfg.db_dir ? `${cfg.aplicada || cfg.ya_configurada ? "configurada" : "propuesta"} — carpeta ${cfg.db_dir}, variable ${cfg.db_env_var}${cfg.db_container ? `, contenedor ${cfg.db_container}` : ""}` : cfg.motivo}
      </Line>
      {(cfg.advertencias || []).map((w) => (
        <p key={w} className="text-xs text-amber-600 dark:text-amber-500">⚠️ {w}</p>
      ))}
    </div>
  );
}

export function ScanPanel({ projectId, server }) {
  const navigate = useNavigate();
  const run = useRunScan(projectId);
  const running = useRef(false); // anti doble clic (además del candado del servidor)
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const handleScan = async () => {
    if (running.current) return;
    running.current = true;
    setError("");
    try {
      await run.mutateAsync(server.id);
      setOpen(false);
    } catch (err) {
      setError(errorMessage(err, "No se pudo analizar el servidor."));
    } finally {
      running.current = false;
    }
  };

  const handleDocument = () =>
    navigate(`/chat/${projectId}`, {
      state: {
        autoMessage: `Documenta en 02_Mapa_del_Sistema.md lo que detectó el análisis del servidor '${server.name}' (sin ningún secreto).`,
      },
    });

  if (!server.last_test_ok && !server.scan_headline) return null; // se ofrece tras una conexión correcta

  return (
    <div data-testid={`scan-panel-${server.name}`} className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          data-testid={`server-scan-${server.name}`}
          size="sm"
          variant="outline"
          onClick={handleScan}
          disabled={run.isPending}
        >
          <ScanSearch className="h-3.5 w-3.5 mr-1.5" />
          {run.isPending ? "Analizando..." : server.scan_headline ? "Volver a analizar" : "Analizar ahora"}
        </Button>
        {run.isPending && <span className="text-xs text-muted-foreground">Solo lectura; puede tardar hasta un minuto.</span>}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {server.scan_headline && !run.isPending && (
        <div className="space-y-1">
          <p data-testid={`scan-headline-${server.name}`} className="text-xs text-emerald-600 dark:text-emerald-500">
            {server.scan_headline}
            {server.scanned_at_mx ? ` — ${server.scanned_at_mx}` : ""}
          </p>
          <div className="flex items-center gap-3">
            <button type="button" className="inline-flex items-center gap-1 text-xs underline" onClick={() => setOpen(!open)}>
              {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {open ? "Ocultar detalle" : "Ver detalle"}
            </button>
            <button type="button" className="inline-flex items-center gap-1 text-xs underline" onClick={handleDocument}>
              <FileText className="h-3 w-3" /> Documentar en Drive
            </button>
          </div>
          {open && <ScanDetail projectId={projectId} server={server} />}
        </div>
      )}
    </div>
  );
}
